<template>
  <v-dialog
    v-if="value"
    :value="value"
    @input="$emit('input', $event)"
    :max-width="maxWidth"
    persistent
    overlay-color="#0f172a"
    overlay-opacity="0.6"
    content-class="ism-pmf-dialog"
    @click:outside="!persistent && $emit('input', false)"
  >
    <div class="ism-pmf-wrap">
      <!-- ── Header ── -->
      <div class="ism-pmf-header">
        <h3 class="ism-pmf-title">{{ title }}</h3>
        <button
          class="ism-pmf-close"
          @click="$emit('input', false)"
          type="button"
        >
          <v-icon size="18" color="var(--accent)">mdi-close</v-icon>
        </button>
      </div>

      <!-- ── Body ── -->
      <div class="ism-pmf-body">
        <slot></slot>
      </div>

      <!-- ── Footer ── -->
      <div class="ism-pmf-footer" v-if="showFooter">
        <button
          class="ism-pmf-btn-cancel"
          type="button"
          :disabled="loading"
          @click="$emit('input', false)"
        >
          {{ cancelText }}
        </button>
        <button
          class="ism-pmf-btn-submit"
          type="button"
          :disabled="loading"
          @click="$emit('submit')"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="14"
            width="2"
            color="#fff"
            style="margin-right: 8px"
          />
          {{ submitText }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "PremiumModalForm",

  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    maxWidth: { type: [Number, String], default: 500 },
    submitText: { type: String, default: "Save Changes" },
    cancelText: { type: String, default: "Cancel" },
    loading: { type: Boolean, default: false },
    showFooter: { type: Boolean, default: true },
    persistent: { type: Boolean, default: true },
  },
};
</script>

<style>
/* Non-scoped — targets v-dialog content-class */
.v-dialog__content {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: center !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
.v-dialog__content::-webkit-scrollbar { display: none !important; }
.v-dialog__content {
  padding-left: 0 !important;
  padding-top: 0 !important;
}

.ism-pmf-dialog {
  border-radius: 18px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22) !important;
  margin: 80px 24px 24px !important;
  align-self: flex-start !important;
  display: flex !important;
  flex-direction: column !important;
  max-height: calc(100vh - 104px) !important;
}

.v-dialog:not(.v-dialog--fullscreen) {
  margin: auto !important;
  margin-top: 80px !important;
}

/* ── Small screens ── */
@media (max-width: 600px) {
  .ism-pmf-dialog {
    margin: 70px 12px 12px !important;
    max-height: calc(100vh - 82px) !important;
    border-radius: 14px !important;
    width: calc(100vw - 24px) !important;
  }
  .v-dialog__content {
    align-items: flex-start !important;
    padding: 0 !important;
  }
}
</style>

<style scoped>
/* ── Wrapper ─────────────────────────────── */
.ism-pmf-wrap {
  display: flex;
  flex-direction: column;
  background: var(--card-bg, #fff);
  border-radius: 18px;
  overflow: hidden;
  font-family: "Inter", sans-serif;
  /* Fill the dialog height so footer always sticks to bottom */
  max-height: inherit;
}

/* ── Header ──────────────────────────────── */
.ism-pmf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  background: var(--bg-primary, #f1f5f9);
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.08));
  flex-shrink: 0;
}

.ism-pmf-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary, #1e293b);
  letter-spacing: -0.01em;
  margin: 0;
}

.ism-pmf-close {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  background: var(--card-bg, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  flex-shrink: 0;
}
.ism-pmf-close:hover {
  background: var(--border-color, rgba(0,0,0,0.07));
  transform: rotate(90deg);
}

/* ── Body ────────────────────────────────── */
.ism-pmf-body {
  padding: 22px;
  overflow-y: auto;
  flex: 1;
  /* Fallback max-height for browsers that don't support flex properly */
  max-height: 65vh;
  background: var(--card-bg, #fff);
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.ism-pmf-body::-webkit-scrollbar { display: none; }

/* ── Footer ──────────────────────────────── */
.ism-pmf-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  background: var(--bg-primary, #f1f5f9);
  border-top: 1px solid var(--border-color, rgba(0,0,0,0.08));
  flex-shrink: 0;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .ism-pmf-body {
    padding: 16px;
    max-height: 55vh;
  }
  .ism-pmf-header { padding: 14px 16px; }
  .ism-pmf-footer {
    padding: 12px 16px;
    gap: 8px;
  }
  .ism-pmf-btn-submit,
  .ism-pmf-btn-cancel {
    flex: 1;
    justify-content: center;
    padding: 10px 12px;
  }
}

/* Submit button */
.ism-pmf-btn-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #f26522, #c94f10);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(242, 101, 34, 0.35);
  white-space: nowrap;
}
.ism-pmf-btn-submit:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(242, 101, 34, 0.45);
}
.ism-pmf-btn-submit:active:not(:disabled) {
  transform: translateY(0);
}
.ism-pmf-btn-submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Cancel button */
.ism-pmf-btn-cancel {
  padding: 10px 20px;
  border-radius: 10px;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  background: var(--card-bg, #fff);
  color: var(--text-secondary, #64748b);
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}
.ism-pmf-btn-cancel:hover:not(:disabled) {
  background: var(--bg-primary, #f1f5f9);
  color: var(--text-primary, #1e293b);
  border-color: var(--border-color2, rgba(0,0,0,0.15));
}
.ism-pmf-btn-cancel:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
